import { z } from 'zod'

import type {
  BodySection,
  Cable,
  CableEventInitializer,
  CableEventsView,
  CableHistoryInitializer,
  CableHistoryMutator,
  CableHistoryView,
  CableInitializer,
  CablePrice,
  LeadSection,
  LegacySiteHistoryLabels,
  OrganizationId,
  ProjectId,
} from '../../index.js'
import { cableHistoryMutator, cableInitializer } from '../../index.js'

export const extendedCableInitializer = z.object({
  history: z
    .array(
      z.object({
        id: z.string().optional(),
        createdAt: z.coerce.date().optional().nullable().optional(),
        createdBy: z.string().optional().nullable().optional(),
        updatedAt: z.coerce.date().optional().nullable().optional(),
        updatedBy: z.string().optional().nullable().optional(),
        deletedAt: z.coerce.date().optional().nullable().optional(),
        deletedBy: z.string().optional().nullable().optional(),
        startedAt: z.coerce.date(),
        endedAt: z.coerce.date().optional().nullable().optional(),
        inventoryStatus: z.string().optional().nullable().optional(),
        organizationId: z.number(),
        projectId: z
          .number()
          .optional()
          .nullable()
          .transform((val) => (val === 0 ? undefined : val)),
      }),
    )
    .optional()
    .nullable(),
  events: z
    .array(
      z.object({
        id: z.string().optional(),
        createdAt: z.coerce.date().optional().nullable().optional(),
        createdBy: z.string().optional().nullable().optional(),
        updatedAt: z.coerce.date().optional().nullable().optional(),
        updatedBy: z.string().optional().nullable().optional(),
        deletedAt: z.coerce.date().optional().nullable().optional(),
        deletedBy: z.string().optional().nullable().optional(),
        action: z.string(),
        notes: z.string().optional().nullable().optional(),
        operator: z.string().optional().nullable().optional(),
        eventDate: z.coerce.date(),
        calibrationId: z.number().optional().nullable().optional(),
      }),
    )
    .optional()
    .nullable(),
  ...(cableInitializer as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ExtendedCableInitializer>

export type ExtendedCableInitializer = CableInitializer & {
  history?:
    | (Omit<CableHistoryInitializer, 'cableId'> & {
        organizationName?: string | null
        projectName?: string | null
      })[]
    | null
  events?: Omit<CableEventInitializer, 'cableId'>[] | null
}

export type ReducedCablePrice = Omit<
  CablePrice,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'createdBy'
  | 'updatedBy'
  | 'deletedAt'
  | 'deletedBy'
>

export type CableSiteUse = {
  id: number
  siteName: string
  projectId: ProjectId
  organizationId: OrganizationId
  cableHistory: Array<{
    startedAt: Date | string
    endedAt: Date | string | null
    cableSerial: number | null
    decode: number[] | null
    labels: LegacySiteHistoryLabels | null
  }>
}

export type ExtendedCable = Cable & {
  lead: LeadSection[]
  body: BodySection[]
  leadLength: number
  bodyLength: number
  sensorCount: number
  history: CableHistoryView[]
  events: CableEventsView[]
  prices: ReducedCablePrice
  sites?: CableSiteUse[]
}

export const bulkCableHistoryMutator = z.array(
  cableHistoryMutator,
) as unknown as z.Schema<CableHistoryMutator[]>

export const filteredCableEventInitializer = z.object({
  id: z.string().optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  action: z.string(),
  notes: z.string().optional().nullable(),
  operator: z.string().optional().nullable(),
  eventDate: z.coerce.date(),
  calibrationId: z.number().optional().nullable(),
}) as unknown as z.Schema<CableEventInitializer>

export const filteredCableHistoryMutator = z.object({
  id: z.string().optional(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  inventoryStatus: z.string().optional().nullable(),
  organizationId: z.number(),
  projectId: z.number(),
}) as unknown as z.Schema<CableHistoryMutator>
